import React from 'react';
import Div from '~/components/atom/Div';
import GridContainer from '~/components/atom/GridContainer';
import Input from '~/components/molecule/Input';
import MultiSelectInput from '~/components/molecule/MultiSelectInput';
import MultiValueInput from '~/components/molecule/MultiValueInput';
import TagListInput from '~/components/molecule/TagListInput';
import Validation from '~/util/Validation';
import normalizeExcludedEmailDomain from '~/components/page/Settings/LFASettings/utils/normalizeExcludedEmailDomain';
import validateExcludedEmailDomain from '~/components/page/Settings/LFASettings/utils/validateExcludedEmailDomain';
import { TEXT, NO_OFFICE_ID, NO_USER_ID } from '../../constants';
import SectionHeaderWithDescription from '../SectionHeaderWithDescription';
import type {
  UpdateSettingsAction,
  UpdateSettingsState,
} from '../../reducers/settingsReducer';
import type { TouchedFields } from '../../hooks/useSettingsValidation';
import useOfficeOptions from '~/hooks/useOfficeOptions';
import useUserOptions from '~/hooks/useUserOptions';
import getAddressString from '~/components/page/LFA/utils/getAddressString';
import { CountryCode } from '~/graphql/types';

const NO_OFFICE_OPTION = {
  label: TEXT.noOfficeLabel,
  key: NO_OFFICE_ID,
  payload: {
    id: NO_OFFICE_ID,
    filterLabel: TEXT.noOfficeLabel,
    tagLabel: TEXT.noOfficeLabel,
  },
};

const NO_USER_OPTION = {
  label: TEXT.noUserLabel,
  key: NO_USER_ID,
  payload: {
    id: NO_USER_ID,
    filterLabel: TEXT.noUserLabel,
    tagLabel: TEXT.noUserLabel,
  },
};

const excludedDomainValidation = [
  (value: string) => {
    if (value.trim() === '') return true;
    return validateExcludedEmailDomain(value).valid
      ? true
      : TEXT.invalidExcludedEmailDomain;
  },
];

type Props = {
  updateSettings: UpdateSettingsState;
  leadScoreErrors: Array<string>;
  tagInputValue: string;
  excludedDomainInputValue: string;
  dispatch: React.Dispatch<UpdateSettingsAction>;
  setTagInputValue: (value: string) => void;
  setExcludedDomainInputValue: (value: string) => void;
  setFieldTouched: (field: keyof TouchedFields) => void;
};

const RegelsGrid: React.FCC<Props> = ({
  updateSettings,
  dispatch,
  leadScoreErrors,
  tagInputValue,
  setTagInputValue,
  excludedDomainInputValue,
  setExcludedDomainInputValue,
  setFieldTouched,
}) => {
  const baseOfficeOptions = useOfficeOptions();
  const baseUserOptions = useUserOptions();

  const officeOptions = [
    NO_OFFICE_OPTION,
    ...baseOfficeOptions.map(option => ({
      label: option.label,
      key: option.key,
      payload: {
        id: option.key,
        filterLabel: option.label + ' (ID: ' + option.key + ')',
        tagLabel:
          option.label +
          (option.payload?.address
            ? ` (${getAddressString({ ...option.payload?.address, countryCode: CountryCode.Nl })})`
            : ` (${TEXT.noAddressFound})`),
        address: option.payload?.address,
      },
    })),
  ];

  const userOptions = [
    NO_USER_OPTION,
    ...baseUserOptions.map(option => ({
      label: option.label,
      key: option.key,
      payload: {
        id: option.key,
        filterLabel: option.label,
        tagLabel: option.label,
      },
    })),
  ];

  return (
    <GridContainer
      columns="1fr 1fr"
      columnGap="m"
      rowGap="xl"
      alignItems="stretch"
      width="100%"
    >
      <Div width="100%" padding={['m', 's']}>
        <SectionHeaderWithDescription
          icon="star"
          header={TEXT.minLeadscoreHeader}
          description={TEXT.minLeadscoreDescription}
        />
        <Input
          type="text"
          value={updateSettings?.rules?.minLeadscore}
          onChange={e => {
            setFieldTouched('leadscore');
            if (!Validation.Number.isNumber(Number(e.target.value))) {
              return;
            }
            dispatch({
              type: 'SET_MIN_LEADSCORE',
              payload: e.target.value,
            });
          }}
          width="100%"
          externalErrors={leadScoreErrors}
          label={{ text: TEXT.numberInputLabel }}
        />
      </Div>

      <Div width="100%" padding={['m', 's']}>
        <SectionHeaderWithDescription
          icon="tag"
          header={TEXT.tagInputHeader}
          description={TEXT.tagInputDescription}
        />
        <Div width="100%">
          <TagListInput
            inputValue={tagInputValue}
            onChange={value => setTagInputValue(value)}
            onAdded={value => dispatch({ type: 'ADD_NOT_TAG', payload: value })}
            onRemoved={value =>
              dispatch({ type: 'REMOVE_NOT_TAG', payload: value })
            }
            selectedValues={updateSettings?.rules?.notTags ?? []}
          />
        </Div>
      </Div>

      <Div width="100%" padding={['m', 's']}>
        <SectionHeaderWithDescription
          icon="mail"
          header={TEXT.excludedEmailDomainsHeader}
          description={TEXT.excludedEmailDomainsDescription}
        />
        <Div width="100%">
          <MultiValueInput
            inputValue={excludedDomainInputValue}
            onChange={setExcludedDomainInputValue}
            label={TEXT.excludedEmailDomainsLabel}
            selectedValues={updateSettings?.rules?.excludedEmailDomains ?? []}
            onAdded={value =>
              dispatch({
                type: 'ADD_EXCLUDED_EMAIL_DOMAIN',
                payload: normalizeExcludedEmailDomain(value),
              })
            }
            onRemoved={value =>
              dispatch({ type: 'REMOVE_EXCLUDED_EMAIL_DOMAIN', payload: value })
            }
            formatFunction={normalizeExcludedEmailDomain}
            validation={excludedDomainValidation}
            addButton={{ label: TEXT.excludedEmailDomainsAdd }}
          />
        </Div>
      </Div>

      <Div width="100%" padding={['m', 's']}>
        <SectionHeaderWithDescription
          icon="office"
          header={TEXT.officeInputHeader}
          description={TEXT.officeInputDescription}
        />
        <MultiSelectInput
          options={officeOptions}
          selectedOptions={officeOptions.filter(option =>
            updateSettings?.rules?.notOfficeId.includes(
              option.payload.id.toString(),
            ),
          )}
          onSelected={option =>
            dispatch({ type: 'ADD_NOT_OFFICE', payload: option.payload.id })
          }
          onRemoved={option =>
            dispatch({
              type: 'REMOVE_NOT_OFFICE',
              payload: option.payload.id,
            })
          }
          width="100%"
          placeholder={TEXT.selectOfficesPlaceholder}
        />
      </Div>

      <Div width="100%" padding={['m', 's']}>
        <SectionHeaderWithDescription
          icon="user"
          header={TEXT.userInputHeader}
          description={TEXT.userInputDescription}
        />
        <MultiSelectInput
          options={userOptions}
          selectedOptions={userOptions.filter(option =>
            updateSettings?.rules?.notUserId.includes(
              option.payload.id.toString(),
            ),
          )}
          onSelected={option =>
            dispatch({ type: 'ADD_NOT_USER', payload: option.payload.id })
          }
          onRemoved={option =>
            dispatch({
              type: 'REMOVE_NOT_USER',
              payload: option.payload.id,
            })
          }
          width="100%"
          placeholder={TEXT.selectUsersPlaceholder}
        />
      </Div>
    </GridContainer>
  );
};

export default RegelsGrid;
